/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useRef, useState } from "react";
import { Camera, Images, Video, Trash2, Loader2, UploadCloud, X, Globe, Play, Download, Pencil } from "lucide-react";
import { toast } from "../../../../components/ui/sonner";
import { Button } from "../../../../components/ui/button";
import { T } from "../../../../lib/i18n";
import { Card, fileUrl, dt } from "./PreFlow";
import FileUploadDate, { uploadDateLabel } from "../FileUploadDate";
import { MediaLightbox } from "../../../../components/MediaLightbox";
import { errTextOf } from "../../../../lib/detailText";
import { normalizeVrUrl } from "../../../../lib/vrLinks";
import { useApp } from "../../../../context/AppContext";

// 能增删 VR 链接的角色，与后端 eng_vr.VR_LINK_ADD_ROLES / VR_LINK_DELETE_ROLES 保持一致。
// 2026-10-22 用户口径「让所有参与人都能自由编辑」：能加的人就能删，不再单独收窄删除。
const VR_DELETE_ROLES = ["admin", "ceo", "eng_manager", "pm", "cdc", "member", "design_manager", "cn_architect", "us_policy"];

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// 需求 FB35f1b8fee0：现场 VR 允许多条（室内 / 室外 / 前后院等）。
// 旧 siteMeasure.vrUrl 只保留一条用于 PDF 和旧页面兼容，新链接统一进 engine_vr；
// 列表去重后展示，避免重复点击「添加」造成同一条链接出现多次。
export const mergeVrLinks = (items = [], legacyUrl = "") => {
  const seen = new Set();
  const links = (items || [])
    .filter((it) => it?.kind === "link" && it?.url)
    .filter((it) => {
      if (seen.has(it.url)) return false;
      seen.add(it.url);
      return true;
    });
  if (legacyUrl && !seen.has(legacyUrl)) links.push({ id: "legacy-vr", kind: "link", url: legacyUrl });
  return links;
};

/** 一次粘贴多条（2026-10-22 用户口径「提交多链接」）：现场交 VR 时室内 / 室外 / 前后院
 *  往往一次给好几条，逐条点「添加」太慢。按换行 / 逗号 / 顿号 / 分号 / 空白切分，
 *  没写协议的补 https://，丢掉不像域名的，最后去重（保持粘贴顺序）。 */
export const parseVrUrls = (raw = "") => {
  const seen = new Set();
  return String(raw)
    .split(/[\s,，、;；]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (/^https?:\/\//i.test(s) ? s : `https://${s}`))
    .filter((s) => { try { return new URL(s).hostname.includes("."); } catch { return false; } })
    .filter((s) => { if (seen.has(s)) return false; seen.add(s); return true; });
};

export const SiteMedia = ({ pre, api, pid, reload }) => {
  const { user } = useApp();
  const canDelVr = VR_DELETE_ROLES.includes(user?.role);
  const sm = pre.siteMeasure || {};
  const media = sm.media || [];
  // 输入框不再回填 sm.vrUrl：历史那条链接已经在下方列表里（mergeVrLinks 会把它并进来），
  // 回填会让人以为还没提交、再点一次就是 409 重复。
  const [vr, setVr] = useState("");
  const [vrBusy, setVrBusy] = useState(false);
  const [vrLinks, setVrLinks] = useState([]);
  const [vrLoading, setVrLoading] = useState(true);
  const [staged, setStaged] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const camRef = useRef(); const galRef = useRef(); const vidRef = useRef();
  // legacyOverride：删掉「历史兼容」那条时，siteMeasure.vrUrl 要等父组件 reload 才刷新，
  // 而列表合并用的是当前的 props，会在删完后把那条又并回来（看起来像没删掉）。
  // 显式传 "" 让本次重取忽略旧字段。
  const loadVrLinks = async (legacyOverride) => {
    setVrLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}/engine/vr`);
      setVrLinks(mergeVrLinks(data.items || [], legacyOverride === undefined ? sm.vrUrl : legacyOverride));
    } catch {
      setVrLinks([]);
    } finally {
      setVrLoading(false);
    }
  };
  useEffect(() => { loadVrLinks(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const stage = (files) => setStaged((s) => [...s, ...Array.from(files || []).map((f) => ({ f, url: URL.createObjectURL(f), video: f.type.startsWith("video/") }))]);
  const upload = async () => {
    if (!staged.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      staged.forEach((s) => fd.append("files", s.f));
      const { data } = await api.post(`/design/v3/projects/${pid}/pre/site-measure/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("已上传 {0} 项现场媒体", [data.added.length]));
      staged.forEach((s) => URL.revokeObjectURL(s.url));
      setStaged([]); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  // 需求 FB35f1b8fee0：现场测量会有室内、室外等多条 VR 链接，原 siteMeasure.vrUrl
  // 只能保存一个字符串。复用项目级 engine_vr 多链接接口保存新增链接，并保留
  // siteMeasure.vrUrl 作为兼容字段（报告 PDF、旧页面仍可读取第一条），避免破坏历史数据。
  // 2026-10-22：改为**一次提交多条** —— 框里粘贴几行就提交几条，走批量接口；
  // 重复 / 格式不对的由后端放进 skipped 返回，不会因为一条重复就整批失败。
  const saveVr = async () => {
    const exist = new Set(vrLinks.map((x) => x.url));
    const urls = parseVrUrls(vr).filter((u) => !exist.has(u));
    if (!urls.length) { toast.message(T("没有新的有效链接（重复或格式不对）")); return; }
    setVrBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/engine/vr/links`, { urls });
      const n = data?.count ?? (data?.added?.length || 0);
      const skipped = data?.skipped?.length || 0;
      if (!sm.vrUrl && n) await api.put(`/design/v3/projects/${pid}/pre/site-measure`, { vrUrl: urls[0] });
      toast.success(skipped ? T("已提交 {0} 条 VR 链接，{1} 条已存在", [n, skipped]) : T("已提交 {0} 条 VR 链接", [n]));
      setVr("");
      await loadVrLinks();
      reload();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail) || e.message);
    }
    setVrBusy(false);
  };
  const del = async (m) => {
    if (!window.confirm(T("删除该媒体？"))) return;
    try { await api.delete(`/design/v3/projects/${pid}/pre/site-measure/media/${m.aid}`); setView((v) => (v === null || media.length <= 1 ? null : Math.min(v, media.length - 2))); reload(); } catch (e) { toast.error(e?.detailText || e.message); }
  };
  const saveCap = async (m, cap) => { try { await api.patch(`/design/v3/projects/${pid}/pre/site-measure/media/${m.aid}`, { caption: cap }); reload(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } };
  // 删一条 VR 链接。两类要分开处理：
  // · engine_vr 里的记录 → DELETE /projects/{pid}/engine/vr/{vid}
  // · legacy-vr 是旧兼容字段 siteMeasure.vrUrl 并进来的，库里没有这条记录，
  //   删它等于把这个字段清空（PUT 传空串会写成 ""，mergeVrLinks 便不再并进来）
  // 另外删 engine_vr 记录时，若兼容字段正好指向同一条，一并清空 ——
  // 否则删完它又会以「历史链接」的身份重新出现在列表里，看起来像删除没生效。
  const delVr = async (it) => {
    if (!window.confirm(T("移除这条 VR 链接？"))) return;
    try {
      let clearedLegacy = false;
      if (it.id === "legacy-vr") {
        await api.put(`/design/v3/projects/${pid}/pre/site-measure`, { vrUrl: "" });
        clearedLegacy = true;
      } else {
        await api.delete(`/projects/${pid}/engine/vr/${it.id}`);
        if (sm.vrUrl && sm.vrUrl === it.url) {
          await api.put(`/design/v3/projects/${pid}/pre/site-measure`, { vrUrl: "" });
          clearedLegacy = true;
        }
      }
      toast.success(T("已移除该 VR 链接"));
      await loadVrLinks(clearedLegacy ? "" : undefined);
      reload();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail) || e.message);
    }
  };
  const [syncing, setSyncing] = useState(false);
  // VR 链接就地编辑（需求方口径「每一条都能编辑删除」）
  const [vrEditId, setVrEditId] = useState("");
  const [vrEditUrl, setVrEditUrl] = useState("");
  const saveVrEdit = async (it) => {
    const full = normalizeVrUrl(vrEditUrl);
    if (!full) { toast.error(T("链接不能为空")); return; }
    try {
      if (it.id === "legacy-vr") {
        // legacy 项不在 engine_vr 库里，改的其实是兼容字段 siteMeasure.vrUrl
        await api.put(`/design/v3/projects/${pid}/pre/site-measure`, { vrUrl: full });
      } else {
        await api.patch(`/projects/${pid}/engine/vr/${it.id}`, { url: full });
        // 兼容字段正好指向同一条时一并改掉，否则它会以「历史链接」身份又冒出来
        if (sm.vrUrl && sm.vrUrl === it.url) {
          await api.put(`/design/v3/projects/${pid}/pre/site-measure`, { vrUrl: full });
        }
      }
      toast.success(T("已保存"));
      setVrEditId("");
      await loadVrLinks(full);
      reload();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail) || e.message);
    }
  };
  const crmCount = media.filter((m) => m.source === "crm_site_eval").length;
  // 框里当前识别到几条（按钮上直接显示「提交 N 条」，粘贴完就知道会提交几条）
  const pendingN = parseVrUrls(vr).filter((u) => !vrLinks.some((x) => x.url === u)).length;
  const syncCrm = async () => {
    setSyncing(true);
    try { const { data } = await api.post(`/design/v3/projects/${pid}/crm-assets/import-site-photos`); toast.success(data.imported ? T("已从 CRM 现场评估同步 {0} 张销售踏勘照片", [data.imported]) : data.reason === "no_site_photos" ? T("CRM 现场评估里没有照片") : T("没有新照片（{0} 张已同步过）", [data.skipped])); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSyncing(false);
  };
  return (
    <Card title={T("现场 VR / 照片 / 视频")} icon={Camera} tid="dv3-pre-sm-media" right={<span className="text-[10px] text-slate-400 inline-flex items-center gap-1.5" data-testid="dv3-pre-sm-media-count">{crmCount > 0 && <span className="px-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid="dv3-pre-sm-crm-count" title={T("来自 CRM 销售踏勘，无需重复拍摄")}>{T("销售踏勘 {0}", [crmCount])}</span>}{T("{0} 项", [media.length])}</span>}>
      {pre.lead?.id && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-sky-800 bg-sky-50/70 border border-sky-100 rounded-lg px-2 py-1.5" data-testid="dv3-pre-sm-crm-box">
          <Download className="h-3.5 w-3.5 text-sky-600" /><span className="flex-1 min-w-[160px]">{crmCount ? T("已同步销售踏勘照片 {0} 张（签约时自动拉取，可随时补拉）", [crmCount]) : T("销售在 CRM「现场评估」拍的照片可直接拉进来，无需设计师重复拍摄")}</span>
          {pre.canEdit && <Button size="sm" variant="outline" className="h-6 text-[10px] border-sky-200 text-sky-700 hover:bg-sky-100" disabled={syncing} onClick={syncCrm} data-testid="dv3-pre-sm-crm-sync">{syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}{T("同步 CRM 踏勘照片")}</Button>}
        </div>
      )}
      <div className="flex items-start gap-1.5">
        <Globe className="h-3.5 w-3.5 text-slate-400 mt-1.5 shrink-0" />
        <textarea data-testid="dv3-pre-sm-vr" disabled={!pre.canEdit} value={vr} onChange={(e) => setVr(e.target.value)} rows={2}
          placeholder={T("VR 全景链接，可一次粘贴多条（每行一条：室内 / 室外 / 前后院…）")}
          className="flex-1 min-w-0 resize-y border border-slate-200 rounded px-1.5 py-1 text-[11px] leading-4" />
        {pre.canEdit && <Button size="sm" variant="outline" className="h-7 text-[10px] shrink-0" disabled={!vr.trim() || vrBusy} onClick={saveVr} data-testid="dv3-pre-sm-vr-save">{vrBusy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}{pendingN > 1 ? T("提交 {0} 条", [pendingN]) : T("提交")}</Button>}
      </div>
      {vrLoading ? (
        <div className="text-[10px] text-slate-400" data-testid="dv3-pre-sm-vr-links-loading">{T("正在读取 VR 链接…")}</div>
      ) : (
        <div className="space-y-1" data-testid="dv3-pre-sm-vr-links" data-count={vrLinks.length}>
          {vrLinks.length > 0 && <p className="text-[10px] font-semibold text-slate-500" data-testid="dv3-pre-sm-vr-links-count">{T("已提交 {0} 条链接", [vrLinks.length])}</p>}
          {vrLinks.length === 0 && <p className="text-[10px] text-slate-400" data-testid="dv3-pre-sm-vr-links-empty">{T("还没有 VR 链接，可在上面的框里一次粘贴多条")}</p>}
          {vrLinks.map((it, i) => (
            <div key={it.id} className="space-y-1" data-testid={`dv3-pre-sm-vr-link-${it.id}`}>
              <div className="flex items-center gap-1.5 rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1 text-[10px]">
                <span className="w-5 shrink-0 text-slate-400">{i + 1}.</span>
                <a href={it.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-violet-700 hover:underline" title={it.url}>{it.url}</a>
                {canDelVr && pre.canEdit && (
                  <>
                    <button type="button" onClick={() => { setVrEditId(vrEditId === it.id ? "" : it.id); setVrEditUrl(it.url || ""); }} title={T("编辑这条链接")}
                      className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-violet-50 hover:text-violet-600 min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center"
                      data-testid={`dv3-pre-sm-vr-edit-${it.id}`}><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => delVr(it)} title={T("移除这条链接")}
                      className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center"
                      data-testid={`dv3-pre-sm-vr-del-${it.id}`}><Trash2 className="h-3 w-3" /></button>
                  </>
                )}
              </div>
              {vrEditId === it.id && canDelVr && pre.canEdit && (
                <div className="flex items-center gap-1.5 pl-6" data-testid={`dv3-pre-sm-vr-edit-form-${it.id}`}>
                  <input value={vrEditUrl} onChange={(e) => setVrEditUrl(e.target.value)} placeholder="https://..."
                    className="min-w-0 flex-1 rounded border border-slate-200 px-1.5 py-1 text-[10px]"
                    data-testid={`dv3-pre-sm-vr-edit-input-${it.id}`} />
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setVrEditId(""); setVrEditUrl(""); }}
                    data-testid={`dv3-pre-sm-vr-edit-cancel-${it.id}`}>{T("取消")}</Button>
                  <Button size="sm" className="h-6 text-[10px] bg-violet-600 hover:bg-violet-700" onClick={() => saveVrEdit(it)}
                    data-testid={`dv3-pre-sm-vr-edit-save-${it.id}`}>{T("保存")}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {pre.canEdit && (
        <div className="flex flex-wrap gap-1.5">
          <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="dv3-pre-sm-cam-input" onChange={(e) => { stage(e.target.files); e.target.value = ""; }} />
          <input ref={galRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="dv3-pre-sm-gallery-input" onChange={(e) => { stage(e.target.files); e.target.value = ""; }} />
          <input ref={vidRef} type="file" accept="video/*" capture="environment" className="hidden" data-testid="dv3-pre-sm-video-input" onChange={(e) => { stage(e.target.files); e.target.value = ""; }} />
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => camRef.current?.click()} data-testid="dv3-pre-sm-cam"><Camera className="h-3.5 w-3.5 mr-1" />{T("拍照")}</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => galRef.current?.click()} data-testid="dv3-pre-sm-gallery"><Images className="h-3.5 w-3.5 mr-1" />{T("相册 / 文件")}</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => vidRef.current?.click()} data-testid="dv3-pre-sm-video"><Video className="h-3.5 w-3.5 mr-1" />{T("拍视频")}</Button>
          {staged.length > 0 && <Button size="sm" className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 ml-auto" disabled={busy} onClick={upload} data-testid="dv3-pre-sm-upload">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />}{T("上传 {0} 项", [staged.length])}</Button>}
        </div>
      )}
      {staged.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="dv3-pre-sm-staged">
          {staged.map((s, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-violet-300 bg-slate-100">
              {s.video ? <video src={s.url} className="h-full w-full object-contain" muted /> : <img src={s.url} alt="" className="h-full w-full object-contain" />}
              <button type="button" onClick={() => setStaged(staged.filter((_, j) => j !== i))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 text-red-600" data-testid={`dv3-pre-sm-staged-rm-${i}`}><X className="h-3 w-3" /></button>
            </div>
          ))}
          <p className="w-full text-[10px] text-slate-400">{T("删选后点「上传」；照片会自动生成缩略图，视频保留原文件")}</p>
        </div>
      )}
      {media.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5" data-testid="dv3-pre-sm-grid">
          {media.map((m, i) => (
            <div key={m.aid} className="group relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`dv3-pre-sm-item-${m.aid}`}>
              <button type="button" onClick={() => setView(i)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 block w-full h-28 bg-slate-800/90" data-testid={`dv3-pre-sm-open-${m.aid}`}>
                {m.kind === "photo" ? <img src={fileUrl(m.thumbFileId || m.fileId)} alt={m.caption || m.name} className="h-full w-full object-contain" loading="lazy" /> : <span className="h-full w-full flex flex-col items-center justify-center text-slate-300 text-[10px]"><Play className="h-6 w-6" />{T("视频")} · {fmtSize(m.size || 0)}</span>}
              </button>
              <div className="flex-wrap px-1 py-0.5 text-[9px] text-slate-500 flex items-center gap-1" title={m.caption || m.name}>{m.source === "crm_site_eval" && <span className="shrink-0 px-1 rounded bg-sky-100 text-sky-700 font-semibold" data-testid={`dv3-pre-sm-crm-badge-${m.aid}`}>CRM</span>}<span className="truncate">{m.caption || m.name}</span><FileUploadDate value={m.uploadedAt} className="basis-full leading-tight" /></div>
              {pre.canEdit && <button type="button" onClick={() => del(m)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`dv3-pre-sm-del-${m.aid}`}><Trash2 className="h-3 w-3" /></button>}
            </div>
          ))}
        </div>
      )}
      {view !== null && media[view] && <MediaLightbox tid="dv3-pre-sm-viewer" index={view} onIndex={setView} onClose={() => setView(null)} canEdit={!!pre.canEdit} onDelete={del} onCaption={saveCap}
        items={media.map((m) => ({ id: m.aid, url: fileUrl(m.fileId), thumbUrl: fileUrl(m.thumbFileId || m.fileId), kind: m.kind === "photo" ? "photo" : "video", name: m.name, caption: m.caption, meta: `${fmtSize(m.size || 0)} · ${m.byName} · ${T("附件记录时间：{0}", [dt(m.at)])} · ${uploadDateLabel(m.uploadedAt)}` }))} />}
    </Card>
  );
};
